import React, { useState, useEffect, useCallback } from "react";
import { Container } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import GlobalMap from "./GlobalMap";
import { hasFeature } from "../../helpers/features";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE = {
  primary: '#ff6b35',
  dark:    '#e85a28',
  light:   '#ff8c61',
};

const card = {
  background:   "var(--vz-secondary-bg, #fff)",
  border:       "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow:    "0 1px 4px rgba(0,0,0,0.06)",
  overflow:     "hidden",
};

const StatPill = ({ label, value, color }) => (
  <div style={{ textAlign: "center", padding: "8px 16px" }}>
    <p style={{ fontSize: 20, fontWeight: 800, color, margin: 0, lineHeight: 1 }}>{value}</p>
    <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--vz-secondary-color)", margin: "3px 0 0" }}>{label}</p>
  </div>
);

const AlertsMap = () => {
  document.title = "Alerts Map | Security Dashboard";

  const [agents,   setAgents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // Summary stats derived from agent data
  const stats = agents.reduce((acc, a) => {
    acc.total    += a.threats?.total    || 0;
    acc.critical += a.threats?.critical || 0;
    acc.high     += a.threats?.high     || 0;
    acc.countries.add(a.country_code);
    return acc;
  }, { total: 0, critical: 0, high: 0, countries: new Set() });

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const flaskUrl = localStorage.getItem("flaskApiUrl") || "";
      const flaskKey = localStorage.getItem("flaskApiKey") || "";
      const flaskHeaders = { "Content-Type": "application/json", "X-API-Key": flaskKey };

      const authUser = JSON.parse(sessionStorage.getItem("authUser") || "{}");
      const nodeHeaders = {
        "Content-Type": "application/json",
        ...(authUser.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
      };

      // Fetch geo data (Flask) + threat stats (Node.js) in parallel
      const [geoRes, threatRes] = await Promise.all([
        fetch(`${flaskUrl}/api/wazuh/agents/geo-map`, { headers: flaskHeaders }),
        fetch(`${API_BASE_URL}/threats/agents/dashboard`,  { headers: nodeHeaders }),
      ]);

      const geoData    = await geoRes.json();
      const threatData = await threatRes.json();

      if (!geoData.success) throw new Error(geoData.error || "Failed to fetch geo data");

      // Build threat lookup map by agent name
      const threatMap = {};
      if (threatData.success) {
        threatData.agents.forEach(a => {
          threatMap[a.name] = {
            total:    a.total    || 0,
            critical: a.critical || 0,
            high:     a.high     || 0,
            medium:   a.medium   || 0,
            low:      a.low      || 0,
          };
        });
      }

      // Merge geo + threats
      const merged = geoData.agents
        .filter(a => a.lat && a.lon)
        .map(a => ({
          ...a,
          threats: threatMap[a.name] || { total: 0, critical: 0, high: 0, medium: 0, low: 0 },
        }));

      setAgents(merged);
      setLastSync(new Date().toLocaleTimeString());

    } catch (e) {
      setError(e.message || "Failed to load map data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="page-content">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Container fluid>

        {/* ── Header ──────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: `${ORANGE.primary}18`, border: `1px solid ${ORANGE.primary}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FeatherIcon icon="globe" size={18} style={{ color: ORANGE.primary }} />
            </div>
            <div>
              <h4 style={{ fontWeight: 800, fontSize: 18, color: "var(--vz-body-color)", margin: 0 }}>Global Threat Map</h4>
              <p style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: 0 }}>
                Real-time attack origins from agent public IPs
                {lastSync && <span style={{ marginLeft: 8, opacity: .6 }}>· Updated {lastSync}</span>}
              </p>
            </div>
          </div>
          <button onClick={fetchData} disabled={loading}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "transparent", color: ORANGE.primary, border: `1px solid ${ORANGE.primary}`, cursor: "pointer", opacity: loading ? .6 : 1 }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = ORANGE.primary; e.currentTarget.style.color = "#fff"; } }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ORANGE.primary; }}>
            <FeatherIcon icon="refresh-cw" size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>

        {/* ── Summary tiles ────────────────────────────────────── */}
        {!loading && agents.length > 0 && (
          <div style={{ ...card, marginBottom: 12, padding: "4px 0" }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", borderBottom: "none" }}>
              <StatPill label="Agents on Map"  value={agents.length}              color={ORANGE.primary} />
              <div style={{ width: 1, height: 40, background: "var(--vz-border-color)" }} />
              <StatPill label="Countries"      value={stats.countries.size}       color="#0284c7"        />
              <div style={{ width: 1, height: 40, background: "var(--vz-border-color)" }} />
              <StatPill label="Total Threats"  value={stats.total.toLocaleString()} color={ORANGE.dark}  />
              <div style={{ width: 1, height: 40, background: "var(--vz-border-color)" }} />
              <StatPill label="Critical"       value={stats.critical.toLocaleString()} color="#8b0000"   />
              <div style={{ width: 1, height: 40, background: "var(--vz-border-color)" }} />
              <StatPill label="High"           value={stats.high.toLocaleString()}    color="#dc3545"    />
              <div style={{ flex: 1 }} />
              {/* Country list */}
              <div style={{ padding: "8px 16px", display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 400 }}>
                {[...stats.countries].filter(Boolean).sort().map(code => (
                  <span key={code} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${ORANGE.primary}10`, color: ORANGE.primary, border: `1px solid ${ORANGE.primary}25` }}>
                    <img src={`https://flagcdn.com/16x12/${code.toLowerCase()}.png`} alt={code}
                      style={{ width: 14, height: 10, borderRadius: 1 }} onError={e => e.target.style.display = "none"} />
                    {code}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────── */}
        {error && (
          <div style={{ ...card, padding: "14px 18px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, color: "var(--vz-danger)", background: "rgba(var(--vz-danger-rgb),.06)", border: "1px solid rgba(var(--vz-danger-rgb),.3)" }}>
            <FeatherIcon icon="alert-circle" size={16} />
            <span style={{ fontSize: 13, flex: 1 }}>{error}</span>
            <button onClick={fetchData} style={{ padding: "5px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: ORANGE.primary, color: "#fff", border: "none", cursor: "pointer" }}>
              Retry
            </button>
          </div>
        )}

        {/* ── Map card ─────────────────────────────────────────── */}
        <div style={{ ...card, height: 560 }}>
          {/* Card header */}
          <div style={{ padding: "13px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FeatherIcon icon="map" size={15} style={{ color: ORANGE.primary }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>Global Threat Visualization</span>
            </div>
            <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", display: "flex", alignItems: "center", gap: 5 }}>
              <FeatherIcon icon="mouse-pointer" size={12} />
              Hover markers for details · Scroll to zoom
            </span>
          </div>

          {/* Map body */}
          <div style={{ height: "calc(100% - 50px)", position: "relative" }}>
            {!loading && agents.length === 0 && !error ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--vz-secondary-color)" }}>
                <FeatherIcon icon="globe" size={48} style={{ opacity: .2, marginBottom: 12 }} />
                <p style={{ fontSize: 15, fontWeight: 600, margin: "0 0 6px" }}>No geo data available</p>
                {hasFeature("geo_location") && (
                  <p style={{ fontSize: 12, opacity: .6, margin: 0 }}>
                    Run <code style={{ color: ORANGE.primary }}>python3 run_daily_ip_update.py</code> to populate agent locations
                  </p>
                )}
              </div>
            ) : (
              <GlobalMap agents={agents} loading={loading} />
            )}
          </div>
        </div>

        {/* ── Top agents by threats ────────────────────────────── */}
        {!loading && agents.length > 0 && (
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ padding: "13px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", gap: 8 }}>
              <FeatherIcon icon="alert-triangle" size={15} style={{ color: ORANGE.primary }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>Top Threat Origins</span>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${ORANGE.primary}12`, color: ORANGE.primary, fontWeight: 600 }}>
                Top 10
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Agent", "Location", "Public IP", "ISP", "Critical", "High", "Total"].map((h, i) => (
                      <th key={i} style={{ padding: "9px 14px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", textAlign: "left", borderBottom: "1px solid var(--vz-border-color)", background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...agents]
                    .sort((a, b) => (b.threats?.total || 0) - (a.threats?.total || 0))
                    .slice(0, 10)
                    .map((agent, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
                        <td style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: ORANGE.primary }}>{i + 1}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${ORANGE.primary}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <FeatherIcon icon="monitor" size={13} style={{ color: ORANGE.primary }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--vz-body-color)" }}>{agent.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--vz-secondary-color)" }}>
                            {agent.country_code && (
                              <img src={`https://flagcdn.com/16x12/${agent.country_code.toLowerCase()}.png`}
                                alt="" style={{ borderRadius: 2 }} onError={e => e.target.style.display = "none"} />
                            )}
                            {[agent.city, agent.country].filter(Boolean).join(", ") || "—"}
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <code style={{ fontSize: 11, color: ORANGE.primary, background: `${ORANGE.primary}10`, padding: "2px 6px", borderRadius: 4, border: `1px solid ${ORANGE.primary}25` }}>
                            {agent.public_ip || "—"}
                          </code>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--vz-secondary-color)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {agent.isp || "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#8b0000" }}>{agent.threats?.critical || 0}</span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#dc3545" }}>{agent.threats?.high || 0}</span>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: ORANGE.primary }}>{(agent.threats?.total || 0).toLocaleString()}</span>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        )}

      </Container>
    </div>
  );
};

export default AlertsMap;